/**
 * The public pages' script, bundled by esbuild into public/build/site.js
 * (npm run build:site). The pages are complete without it; this adds the
 * clock, the motion and the controls. Each widget takes its data as an
 * argument; this file is the only one that reads the sample data, which
 * only the labelled sample alerts show. The live raises come with the page.
 */
import { DEALS, EXITS, FREETRADE, STORIES, TIMELINE } from '../data';
import { DEFAULT_RULE, type RuleGroup } from '../rules';
import { alertTypes } from './alerts';
import { browseSection } from './browse';
import { dataPage } from './data-page';
import { $, $$ } from './dom';
import { momentumSection } from './momentum';
import { reveal } from './motion';
import { onboardingPage } from './onboarding';
import { pricingPage, pricingTeaser } from './pages';
import { aboutPromo } from './promo';
import { platformsPage } from './platforms';
import { raisePage } from './raise';
import { raisesPage } from './raises';
import { roadmap } from './roadmap';
import { inTurn } from './turns';
import { weeklyCard } from './weekly';
import {
  accordion,
  exitsWidget,
  flashlight,
  freeCard,
  freetrade,
  liveFeed,
  proofStrip,
  ruleBuilder,
  signup,
  stepper,
  stories,
  themeToggle,
  timeline,
  waffle,
} from './widgets';

/**
 * Starts the widgets of whatever page this is, each in a task of its own
 * (turns.ts). head.hbs loads this script once the page has loaded and
 * painted, after the whole stylesheet, so nothing here delays the first
 * paint, and the page is complete and usable before it runs.
 */
function init() {
  const doc = document;
  /** The widget on the first element `selector` finds, if there is one. */
  const one =
    <T extends Element = HTMLElement>(
      selector: string,
      widget: (el: T) => void,
    ) =>
    () => {
      const el = $<T>(doc, selector);
      if (el) widget(el);
    };
  /** The widget on every element `selector` finds. */
  const each =
    <T extends Element = HTMLElement>(
      selector: string,
      widget: (el: T) => void,
    ) =>
    () => {
      for (const el of $$<T>(doc, selector)) widget(el);
    };

  inTurn([
    each('[data-theme-toggle]', themeToggle),
    () => reveal(doc),

    one('[data-stories]', (el) => stories(el, STORIES)),
    one('[data-signup]', signup),
    one('[data-weekly]', weeklyCard),
    one('[data-feed]', liveFeed),
    one('.proof-grid', proofStrip),
    one('[data-momentum]', momentumSection),
    one('[data-exits]', (el) => exitsWidget(el, EXITS)),
    one('[data-waffle]', (el) => waffle(el, $(doc, '[data-outcomes]'))),
    one('[data-freetrade]', (el) => freetrade(el, [...FREETRADE])),
    one('[data-flashlight]', flashlight),

    one('[data-stepper]', (how) => {
      const rule = $(how, '[data-rule]');
      if (rule) ruleBuilder(rule, ruleGroupsIn(rule), DEFAULT_RULE);
      const tl = $(how, '[data-timeline]');
      const line = tl ? timeline(tl, TIMELINE) : null;
      stepper(how, (panel) => (panel === 2 ? line?.start() : line?.stop()));
    }),

    one('[data-alert]', (el) => alertTypes(el, DEALS)),
    one('[data-browse]', browseSection),
    one('[data-roadmap]', roadmap),
    each<HTMLAnchorElement>('[data-free-cta]', freeCard),
    one('[data-teaser]', pricingTeaser),
    each('[data-accordion]', accordion),

    one('[data-pricing]', pricingPage),
    one('[data-onboarding]', onboardingPage),
    one('[data-data-page]', dataPage),
    one('[data-platforms]', platformsPage),
    one('[data-raise]', (el) => raisePage(el)),
    one('[data-raises]', raisesPage),
    each('[data-promo]', aboutPromo),
    one('[data-account-deleted]', accountDeleted),
  ]);
}

/**
 * The live raises the rule builder matches, as the page drew them
 * (landingView's rule.groups); null if the page has none or they do not
 * parse.
 */
function ruleGroupsIn(root: HTMLElement): RuleGroup[] | null {
  const data = $(root, '[data-rule-live]');
  if (!data?.textContent) return null;
  try {
    const groups: unknown = JSON.parse(data.textContent);
    return Array.isArray(groups) ? (groups as RuleGroup[]) : null;
  } catch {
    return null;
  }
}

/**
 * The dashboard sends someone who has just deleted their account here with
 * ?account=deleted (Settings → Manage account). Read here rather than on the
 * server, so the page stays one cacheable copy; the parameter goes once
 * read, so a reload or a shared link does not say it again.
 */
function accountDeleted(line: HTMLElement) {
  const url = new URL(window.location.href);
  if (url.searchParams.get('account') !== 'deleted') return;
  line.hidden = false;
  url.searchParams.delete('account');
  window.history.replaceState(window.history.state, '', url.toString());
}

init();
